import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  PlusCircleOutlined,
  RocketOutlined,
  ShopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Timeline,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { IAwb, IFilterAwb } from "../../models/Tracking";
import {
  useAddAwbStatus,
  useGetAwb,
  useGetAwbDetail,
  useUpdateAwbStatus,
} from "../../query/Tracking";
import { notify } from "../../utils/toast";

const { Text, Title } = Typography;

const AwbManage = () => {
  const [filter, setFilter] = useState<IFilterAwb>({
    page: 1,
    limit: 10,
  });
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [openAddStatusModal, setOpenAddStatusModal] = useState<boolean>(false);
  const [awbId, setAwbId] = useState<number>();
  const [formData, setFormData] = useState({
    description: "",
    location: "",
    status: "" as IAwb["awbStatus"],
    eventTime: null as any,
  });
  const { data, isLoading } = useGetAwb(filter);
  const { data: awbDetail, isLoading: awbDetailLoading } = useGetAwbDetail(
    awbId!
  );
  const addAwbStatus = useAddAwbStatus();
  const updateAwbStatus = useUpdateAwbStatus();
  // eslint-disable-next-line
  const handleSearch = useCallback(
    debounce((value: string) => {
      setFilter((prev) => ({
        ...prev,
        awbCode: value || undefined,
        page: 1,
      }));
    }, 500),
    []
  );

  const handleAddAwbStatus = async () => {
    if (
      !formData.description ||
      !formData.location ||
      !formData.status ||
      !formData.eventTime
    ) {
      return;
    }
    try {
      await addAwbStatus.mutateAsync({
        awbId: awbId!,
        status: formData.status,
        description: formData.description,
        location: formData.location,
        eventTime: formData.eventTime,
      });
      notify.success("Thêm trạng thái thành công");
    } catch (error) {
      notify.error("Thêm trạng thái thất bại");
    }
    setOpenAddStatusModal(false);

    setFormData({
      description: "",
      location: "",
      status: "" as IAwb["awbStatus"],
      eventTime: null,
    });
  };
  const renderStatus = (status: IAwb["awbStatus"]) => {
    switch (status) {
      case "CREATED":
        return <Tag color="blue">Đã tạo</Tag>;
      case "DELIVERED":
        return <Tag color="green">Đã giao</Tag>;
      case "IN_TRANSIT":
        return <Tag color="orange">Đang vận chuyển</Tag>;
      case "PICKED_UP":
        return <Tag color="cyan">Đã lấy hàng</Tag>;
      case "ARRIVED_AT_HUB":
        return <Tag color="purple">Đã đến bưu cục</Tag>;
      case "OUT_FOR_DELIVERY":
        return <Tag color="gold">Đang giao hàng</Tag>;
      case "FAILED":
        return <Tag color="red">Giao thất bại</Tag>;
      default:
        return <Tag color="default">Hoàn hàng</Tag>;
    }
  };

  const getStatusColor = (status: IAwb["awbStatus"] | null) => {
    switch (status) {
      case "CREATED":
        return "blue";
      case "DELIVERED":
        return "green";
      case "IN_TRANSIT":
        return "orange";
      case "PICKED_UP":
        return "cyan";
      case "ARRIVED_AT_HUB":
        return "purple";
      case "OUT_FOR_DELIVERY":
        return "gold";
      case "FAILED":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusIcon = (status: IAwb["awbStatus"] | null) => {
    switch (status) {
      case "CREATED":
        return <ClockCircleOutlined />;
      case "DELIVERED":
        return <CheckCircleOutlined />;
      case "IN_TRANSIT":
        return <RocketOutlined />;
      case "PICKED_UP":
        return <ShopOutlined />;
      case "ARRIVED_AT_HUB":
        return <ShopOutlined />;
      case "OUT_FOR_DELIVERY":
        return <CarOutlined />;
      case "FAILED":
        return <CloseCircleOutlined />;
      default:
        return <SyncOutlined />;
    }
  };
  const column: TableColumnType<IAwb>[] = [
    {
      title: "Mã vận đơn",
      dataIndex: "awbCode",
      key: "awbCode",
      align: "center",
      filterDropdown: () => {
        return (
          <div className="p-4">
            <Input
              placeholder="Mã vận đơn"
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Mã nhà vận chuyển",
      dataIndex: "carrierCode",
      key: "carrierCode",
      align: "center",
      filterDropdown: () => {
        return (
          <div className="p-4">
            <Select
              placeholder="Mã nhà vận chuyển"
              onChange={(value) => {
                setFilter({
                  ...filter,
                  carrierCode: value,
                  page: 1,
                });
              }}
              allowClear
              className="w-48"
              options={[
                {
                  value: "DHL",
                  label: "DHL",
                },
                {
                  value: "FEDEX",
                  label: "FEDEX",
                },
              ]}
            />
          </div>
        );
      },
    },
    {
      title: "Tên đơn vị vận chuyển",
      dataIndex: "carrierName",
      key: "carrierName",
      align: "center",
    },
    {
      title: "Trạng thái vận đơn",
      dataIndex: "awbStatus",
      key: "awbStatus",
      align: "center",
      render: (value: IAwb["awbStatus"]) => {
        return renderStatus(value);
      },
    },
    {
      title: "Hành động",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (_any, record: IAwb) => {
        return (
          <Button
            type="link"
            onClick={() => {
              setAwbId(record.id);
              setOpenDetailModal(true);
            }}
          >
            Xem chi tiết
          </Button>
        );
      },
    },
  ];
  return (
    <div className="p-4">
      <Table
        dataSource={data?.data}
        columns={column}
        loading={isLoading}
        pagination={{
          current: data?.pagination.page,
          pageSize: data?.pagination.limit,
          total: data?.pagination.total,
          onChange: (page, pageSize) => {
            setFilter({
              ...filter,
              page,
              limit: pageSize,
            });
          },
        }}
        scroll={{ x: 1200 }}
      />
      <Modal
        title="Chi tiết vận đơn"
        open={openDetailModal}
        onCancel={() => setOpenDetailModal(false)}
        width={900}
        footer={
          <Space>
            <Button onClick={() => setOpenDetailModal(false)}>Đóng</Button>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => setOpenAddStatusModal(true)}
            >
              Thêm trạng thái
            </Button>
            <Popconfirm
              title="Bạn có chắc chắn đồng bộ dữ liệu"
              onConfirm={async () => {
                try {
                  if (awbDetail?.carrierCode === "DHL") {
                    await updateAwbStatus.mutateAsync(awbDetail.awbCode);
                    notify.success("Đồng bộ dữ liệu thành công");
                    setOpenDetailModal(false);
                  } else {
                    notify.info("Tính năng chỉ dành cho DHL Services");
                  }
                } catch (error) {
                  notify.error("Đồng bộ dữ liệu thất bại");
                }
              }}
              okText="Đồng bộ"
              cancelText="Hủy"
            >
              <Button
                color="green"
                icon={<SyncOutlined />}
                loading={updateAwbStatus.isPending}
                variant="solid"
              >
                Đồng bộ
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        {awbDetailLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" tip="Đang tải chi tiết..." />
          </div>
        ) : !awbDetail ? (
          <div className="flex justify-center items-center py-20">
            <Text type="secondary" className="text-base">
              Không tìm thấy thông tin vận đơn
            </Text>
          </div>
        ) : (
          <div className="space-y-6">
            <Card className="bg-blue-50 dark:bg-blue-900">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Text type="secondary" className="text-sm">
                    Mã vận đơn
                  </Text>
                  <Title level={4} className="mt-1 mb-0">
                    {awbDetail.awbCode}
                  </Title>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Trạng thái
                  </Text>
                  <div className="mt-1">
                    {renderStatus(awbDetail.awbStatus)}
                  </div>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Mã nhà vận chuyển
                  </Text>
                  <Title level={5} className="mt-1 mb-0">
                    {awbDetail.carrierCode}
                  </Title>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Tên nhà vận chuyển
                  </Text>
                  <Title level={5} className="mt-1 mb-0">
                    {awbDetail.carrierName}
                  </Title>
                </div>
              </div>
            </Card>

            <Divider className="my-4" />

            <div className="max-h-96 overflow-y-auto p-1">
              {awbDetail.histories && awbDetail.histories.length > 0 ? (
                <Timeline
                  items={awbDetail.histories.map((history) => ({
                    color: getStatusColor(history.status),
                    dot: getStatusIcon(history.status),
                    children: (
                      <div className="pb-4">
                        <Text strong className="block text-base mb-2">
                          {history.description}
                        </Text>
                        <Space className="mt-2" wrap>
                          <Tag icon={<ClockCircleOutlined />}>
                            {dayjs(history.eventTime).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                          </Tag>
                          <Tag icon={<EnvironmentOutlined />}>
                            {history.location}
                          </Tag>
                          <Tag color="blue">
                            {history.status === "DELIVERED" && "Đã giao"}
                            {history.status === "IN_TRANSIT" &&
                              "Đang vận chuyển"}
                            {history.status === "CREATED" && "Đã tạo"}
                            {history.status === "PICKED_UP" && "Đã lấy hàng"}
                            {history.status === "ARRIVED_AT_HUB" &&
                              "Đã đến bưu cục"}
                            {history.status === "OUT_FOR_DELIVERY" &&
                              "Đang giao hàng"}
                            {history.status === "FAILED" && "Giao thất bại"}
                            {!history.status && "Hoàn hàng"}
                          </Tag>
                          {history.source && (
                            <Tag color="default">
                              {history.source === "MANUAL" && "Thủ công"}
                              {history.source === "API" && "Tự động"}
                            </Tag>
                          )}
                        </Space>
                      </div>
                    ),
                  }))}
                />
              ) : (
                <div className="text-center py-8">
                  <Text type="secondary">Chưa có lịch sử vận chuyển</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Thêm trạng thái vận đơn"
        open={openAddStatusModal}
        onCancel={() => {
          setOpenAddStatusModal(false);
          setFormData({
            description: "",
            location: "",
            status: "" as IAwb["awbStatus"],
            eventTime: null,
          });
        }}
        footer={null}
      >
        <div className="space-y-4">
          <div>
            <Text className="block mb-2">Sự kiện</Text>
            <Input
              placeholder="Nhập sự kiện"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
          <div>
            <Text className="block mb-2">Vị trí hiện tại</Text>
            <Input
              placeholder="Nhập vị trí"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
            />
          </div>
          <div>
            <Text className="block mb-2">Trạng thái</Text>
            <Select
              className="w-full"
              placeholder="Nhập trạng thái"
              value={formData.status || undefined}
              onChange={(value) => setFormData({ ...formData, status: value })}
              options={[
                { label: "Đã tạo", value: "CREATED" },
                { label: "Đã lấy hàng", value: "PICKED_UP" },
                { label: "Đã đến bưu cục", value: "ARRIVED_AT_HUB" },
                { label: "Đang vận chuyển", value: "IN_TRANSIT" },
                { label: "Đang giao hàng", value: "OUT_FOR_DELIVERY" },
                { label: "Đã giao", value: "DELIVERED" },
                { label: "Giao thất bại", value: "FAILED" },
              ]}
            />
          </div>
          <div>
            <Text className="block mb-2">Thời gian</Text>
            <DatePicker
              className="w-full"
              showTime
              placeholder="Nhập thời gian"
              value={formData.eventTime}
              onChange={(value) =>
                setFormData({ ...formData, eventTime: value })
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setOpenAddStatusModal(false);
                setFormData({
                  description: "",
                  location: "",
                  status: "" as IAwb["awbStatus"],
                  eventTime: null,
                });
              }}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              onClick={handleAddAwbStatus}
              loading={addAwbStatus.isPending}
              disabled={
                !formData.description ||
                !formData.location ||
                !formData.status ||
                !formData.eventTime
              }
            >
              Thêm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AwbManage;
